import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SharedForm from "@widgets/SharedForm";
import SnGrid from "@shared/components/GridV2/SnGrid";

import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SN_PRD_DIE_REG_001_POP_01 from "./SN_PRD_DIE_REG_001_POP_01";
import { dateToString } from "@shared/utils/DateUtils";
interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

const dieDateFields = [
  "dt_mfg",
  "dt_start",
  "dt_ppap",
  "dt_ppap_approve",
  "dt_ptr_approve",
  "dt_end",
];

const normalizeDieDateFields = (row: any) => {
  const normalizedRow = { ...row };
  dieDateFields.forEach((field) => {
    normalizedRow[field] = String(row?.[field] ?? "").trim();
  });
  return normalizedRow;
};

const normalizeDieRows = (rows: any) =>
  Array.isArray(rows) ? rows.map(normalizeDieDateFields) : rows;

const SN_PRD_DIE_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region Variable
  const gridref1 = useRef<any>(null);
  const gridref2 = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const pendingSelectedCdDieRef = useRef<string | null>(null);
  const { userInfo } = global();
  //#endregion

  //#region Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColums1] = useState<any[]>([]);
  const [columns2, setColums2] = useState<any[]>([]);
  const [sharedForm, setSharedForm] = useState<any[]>([]);
  const [dataSource1, setDataSource1] = useState<any>();
  const [dataSource2, setDataSource2] = useState<any>();
  const SharedFormref1 = useRef<any>();
  const [focusedRowData, setFocusedRowData] = useState<any>();
  //#endregion

  //#region grid 길이 관련
  const cd_menu = Menuprops.cd_menu || "SN_PRD_DIE_REG_001";
  const defaultSize = {
    panel1: "22.5%",
    panel2: "49.5%",
    panel3: "49.2%",
  };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      setDataSource1([]);
      setDataSource2([]);
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setSharedForm(rtn.form.freeform1);
        setColums1(rtn.grid.grid1);
        setColums2(rtn.grid.grid2);
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region Button Events
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));
  //#endregion

  const reloadDieData = async (preferredCdDie?: string) => {
    const data: any = await fnGetDataSet({
      id: "sn_prd_die_reg_001",
      params: searchRef.current.getFormdata(),
    });
    const dieRows = normalizeDieRows(data[0]) ?? [];
    const dieHistoryRows = normalizeDieRows(data[1]) ?? [];
    const targetRow =
      dieRows.find(
        (row: any) => String(row.cd_die) === String(preferredCdDie)
      ) ?? dieRows[0];

    pendingSelectedCdDieRef.current = targetRow?.cd_die ?? null;
    gridref1.current?.resetUpdate();
    gridref2.current?.resetUpdate();
    setDataSource1(dieRows);
    setDataSource2(dieHistoryRows);
  };

  const onDieRowsReady = useCallback(() => {
    const targetCdDie = pendingSelectedCdDieRef.current;
    const grid = gridref1.current;
    pendingSelectedCdDieRef.current = null;
    if (!targetCdDie || !grid) return;

    for (let index = 0; index < grid.getDisplayedRowCount(); index++) {
      const rowNode = grid.getRowNodeAtIndex(index);
      if (String(rowNode?.data?.cd_die) === String(targetCdDie)) {
        grid.selectAndFocusRow(index);
        gridref2.current?.applyFilters({ cd_die: targetCdDie });
        return;
      }
    }
  }, []);

  //#region onSearch
  const onSearch = async () => {
    const modifiedRows = gridref1.current?.getChange();
    if (modifiedRows?.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave(false);
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      await reloadDieData(focusedRowData?.cd_die);
    } catch (error: any) {}
  };
  //#endregion

  //#region onInsert
  const onInsert = async () => {
    try {
      gridref1.current.addRow();
      setTimeout(() => {
        const grid = gridref1.current;
        if (!grid?.api) return;
        grid.api.setFocusedCell(grid.api.getDisplayedRowCount() - 1, "cd_die");
      }, 50);
    } catch (error: any) {}
  };
  //#endregion

  //#region onBeforeAddRow1
  const onBeforeAddRow1 = () => {
    try {
      const searchData = searchRef.current?.getFormdata?.() ?? {};
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        yn_use: "Y",
        st_die: "1",
        qt_cavitiy: 1,
        qt_pitch: 1,
        cd_exch: "000",
        rt_exch: 1,
        dt_start: today,
        no_hst_sales: 1,
      };
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    try {
      if (!gridref1.current) return;

      const confirm = await showDialog("confirm", "삭제하시겠습니까?");
      if (!confirm) return;

      gridref1.current.deleteSelectedRows();

      const realDeletedRows = gridref1.current.getChange().delete ?? [];
      if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
        // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
        gridref1.current.resetUpdate();
        return;
      }

      return await onSave();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };
  //#endregion

  const toDieHstUpdateRow = (row: any) => ({
    cd_firm: row.cd_firm,
    cd_die: row.cd_die,
    no_hst: Number(row.no_hst),
    qt_cavity: row.qt_cavitiy,
    qt_pitch: row.qt_pitch,
    st_die: row.st_die,
    qt_shot_guaranty: row.qt_shot_guaranty,
    qt_shot: row.qt_shot,
    dt_start: row.dt_start,
    dt_ppap: row.dt_ppap,
    dt_ppap_approve: row.dt_ppap_approve,
    dt_ptr_approve: row.dt_ptr_approve,
    cd_cust_mfg: row.cd_cust_mfg,
    nm_cust_mfg: row.nm_cust_mfg,
    dt_mfg: row.dt_mfg,
    am_mfg: row.am_mfg,
    cd_reason: row.cd_reason,
    dt_end: row.dt_end,
    dc_reason_end: row.dc_reason_end,
  });

  //#region onSave
  const onSave = async (reloadAfterSave = true) => {
    try {
      const requiredValuesCheck = gridref1.current.requiredValuesCheck();

      if (!requiredValuesCheck) {
        return;
      }

      const die = gridref1.current.getChange();

      if (die.modifiedRowcount === 0) {
        await showDialog("alter", "저장할 내용이 없습니다.");
        return;
      }

      const updatedRows = die.update ?? [];

      const dieHstUpdates = updatedRows.map(toDieHstUpdateRow);
      const die_hst = {
        insert: [],
        update: dieHstUpdates,
        delete: [],
        modifiedRowcount: dieHstUpdates.length,
      };

      const ok = await fnSave("SN_PRD_DIE_REG_001_SAVE_01", { die, die_hst });
      if (!ok) return false;

      const targetRow =
        gridref1.current?.getSelectedRows?.()?.[0] ??
        die.insert?.[0] ??
        updatedRows[0] ??
        focusedRowData ??
        die.delete?.[0];

      gridref1.current.resetUpdate();
      if (reloadAfterSave) {
        await reloadDieData(targetRow?.cd_die);
      }
      return true;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  const onSaveDieHst = async (data: any) => {
    const selectedRow =
      gridref1?.current?.getSelectedRows?.()?.[0] ?? focusedRowData;
    if (!selectedRow) return false;

    const noHst = Number(data.no_hst);
    const mainRow = {
      ...selectedRow,
      ...data,
      no_hst: noHst,
      qt_cavitiy: data.qt_cavity,
    };
    delete mainRow.qt_cavity;
    const hstRow = {
      cd_firm: selectedRow.cd_firm,
      cd_die: selectedRow.cd_die,
      ...data,
      no_hst: noHst,
    };

    const ok = await fnSave("SN_PRD_DIE_REG_001_SAVE_01", {
      die: { insert: [], update: [mainRow], delete: [], modifiedRowcount: 1 },
      die_hst: {
        insert: [hstRow],
        update: [],
        delete: [],
        modifiedRowcount: 1,
      },
    });

    if (!ok) return false;
    return true;
  };

  //#region onGrid1BtnsClick
  const onGrid1BtnsClick = async (id: string) => {
    const selectedRow = gridref1?.current?.getSelectedRows?.();
    if (!selectedRow?.length) {
      await showDialog("alter", "행을 선택하세요.");
      return;
    }
    if (id == "btnAdd") {
      const component = (
        <SN_PRD_DIE_REG_001_POP_01
          rowData={selectedRow[0]}
          onSave={onSaveDieHst}
        />
      );
      const result = await showPopupModal({
        title: "차수 추가",
        children: component,
        width: "980px",
        height: "640px",
        okayBtnText: "확인",
        checkData: false,
      });

      if (result?.dieHstAdd) {
        await onSearch();
        requestAnimationFrame(() => {
          gridref2.current?.applyFilters({ cd_die: selectedRow[0].cd_die });
        });
      }
      return;
    }
  };
  //#endregion

  //#region onFocusedRowChanged
  const onFocusedRowChanged = useCallback((focusedRowData: any | null) => {
    setFocusedRowData(focusedRowData);

    if (focusedRowData) {
      gridref2.current.applyFilters({ cd_die: focusedRowData.cd_die });
    }
  }, []);
  //#endregion

  //#region onFormValueChanged
  const onFormValueChanged = (data: any, param: string, formData: any) => {
    const toNumber = (value: any) =>
      Number(String(value ?? "").replace(/,/g, "")) || 0;
    const getChangedValue = () =>
      data && typeof data === "object" && param in data ? data[param] : data;

    if (param === "cd_item") {
      return {
        nm_item: data?.nm_item ?? data?.name ?? "",
        dc_item_spec: data?.dc_item_spec ?? "",
        dc_item_unit: data?.dc_item_unit ?? "",
      };
    }

    if (param === "am" || param === "rt_exch") {
      const value = getChangedValue();
      const am = toNumber(param === "am" ? value : formData?.am);
      const rtExch = toNumber(param === "rt_exch" ? value : formData?.rt_exch);

      return {
        am_k: am * rtExch,
      };
    }

    return {};
  };
  //#endregion

  //#region UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex w-full h-full min-w-0 space-x-1.5">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
          minHeight={130}
        >
          <SnGrid
            ref={gridref1}
            columnConfig={columns1} // 컬럼 설정 전달
            rowData={dataSource1}
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "금형정보",
            }}
            events={{
              onFocusedRowChanged: onFocusedRowChanged,
              onBeforeAddRow: onBeforeAddRow1,
              onRowDataReady: onDieRowsReady,
            }}
          />
        </SnResizable>
        <div className="flex-1 h-full min-h-0 space-y-1.5 overflow-y-hidden">
          <SnResizable
            direction={"height"}
            seq={2}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panel2}
            onResizeStop={onResizeStop}
            enable={{ bottom: true }}
            minHeight={130}
            minWidth={130}
          >
            <SharedForm
              ref={SharedFormref1}
              data={focusedRowData}
              controls={sharedForm}
              gridref={gridref1}
              onValueChaged={onFormValueChanged}
              title="상세정보"
              header={
                <div
                  className="w-full relative float-start space-x-20"
                  style={{ float: "right" }}
                >
                  <div>
                    <SnButton
                      text={"차수 추가"}
                      onClick={() => {
                        onGrid1BtnsClick("btnAdd");
                      }}
                    />
                  </div>
                </div>
              }
            ></SharedForm>
          </SnResizable>
          <SnResizable
            direction={"height"}
            seq={3}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panel3}
            onResizeStop={onResizeStop}
            enable={{ bottom: true }}
            minHeight={130}
            minWidth={130}
          >
            <SnTab
              titles={["차수이력"]}
              width={"100%"}
              header={[<SnTabHeader />]}
              body={[
                <SnGrid
                  ref={gridref2}
                  columnConfig={columns2}
                  rowData={dataSource2}
                  etcProps={{
                    isEditable: true,
                    rowSelection: "single",
                  }}
                />,
              ]}
            />
          </SnResizable>
        </div>
      </div>
    </div>
  );
  //#endregion
});

export default SN_PRD_DIE_REG_001;
